import { fetchConfig } from "@theme/utilities";

class RaiselyProfileSelector extends HTMLElement {
  #profiles = [];

  connectedCallback() {
    if (this.dataset.initialized) return;
    this.dataset.initialized = "true";

    this.loading = this.querySelector("[data-raisely-loading]");
    this.search = this.querySelector("[data-raisely-search]");
    this.input = this.querySelector("[data-raisely-input]");
    this.searchButton = this.querySelector("[data-raisely-search-button]");
    this.suggestions = this.querySelector("[data-raisely-suggestions]");
    this.selected = this.querySelector("[data-raisely-selected]");
    this.profileId = this.querySelector("[data-raisely-profile-id]");
    this.error = this.querySelector("[data-raisely-error]");

    this.input?.addEventListener("input", () => this.#renderSuggestions());
    this.searchButton?.addEventListener("click", () => this.#renderSuggestions());
    this.suggestions?.addEventListener("click", (event) => this.#selectProfile(event));
    this.selected?.addEventListener("click", (event) => this.#editProfile(event));
    document.addEventListener("click", (event) => {
      if (!this.contains(event.target)) this.suggestions?.classList.add("hidden");
    });

    this.#loadProfiles();
  }

  async #loadProfiles(retries = 3) {
    try {
      const response = await fetch(`${this.dataset.apiBaseUrl}/profiles?campaignMode=LIVE`);
      if (!response.ok) throw new Error(`Profile request failed with ${response.status}`);

      const profiles = await response.json();
      if (!Array.isArray(profiles) || profiles.length === 0) throw new Error("No Raisely profiles returned");

      this.#profiles = profiles;
      this.loading?.classList.add("hidden");
      this.search?.classList.remove("hidden");

      if (this.profileId?.value) await this.#showSelectedProfile(this.profileId.value);
    } catch (error) {
      console.error("Unable to load Raisely profiles", error);
      if (retries > 0) {
        window.setTimeout(() => this.#loadProfiles(retries - 1), 2000);
        return;
      }

      this.loading?.classList.add("hidden");
      this.error.textContent = "Unable to load fundraisers. Please try again later.";
      this.error?.classList.remove("hidden");
    }
  }

  #renderSuggestions() {
    const query = this.input?.value.trim().toLowerCase();
    this.suggestions.replaceChildren();

    if (!query) {
      this.suggestions.classList.add("hidden");
      return;
    }

    const matches = this.#profiles.filter((profile) => profile.text?.toLowerCase().includes(query)).slice(0, 50);
    if (!matches.length) {
      const item = document.createElement("li");
      item.textContent = `No fundraisers found for “${this.input.value}”.`;
      this.suggestions.append(item);
    } else {
      matches.forEach((profile) => {
        const item = document.createElement("li");
        const button = document.createElement("button");
        button.type = "button";
        button.dataset.profileId = profile.value;
        button.textContent = profile.text;
        item.append(button);
        this.suggestions.append(item);
      });
    }

    this.suggestions.classList.remove("hidden");
  }

  async #selectProfile(event) {
    const button = event.target.closest("button[data-profile-id]");
    if (!button) return;

    const profileId = button.dataset.profileId;
    this.profileId.value = profileId;
    this.suggestions.classList.add("hidden");
    await this.#saveCartAttribute(profileId);
    await this.#showSelectedProfile(profileId);
  }

  async #showSelectedProfile(profileId) {
    try {
      this.search.classList.add("hidden");
      this.selected.classList.remove("hidden");
      this.selected.textContent = "Loading fundraiser…";

      const response = await fetch(`${this.dataset.apiBaseUrl}/profiles/${encodeURIComponent(profileId)}/shopify-fragment`);
      if (!response.ok || response.status === 204) throw new Error(`Profile fragment request failed with ${response.status}`);

      this.selected.innerHTML = await response.text();
    } catch (error) {
      console.error("Unable to load Raisely profile", error);
      this.selected.classList.add("hidden");
      this.search.classList.remove("hidden");
      this.error.textContent = "Unable to load the selected fundraiser. Please select it again.";
      this.error.classList.remove("hidden");
    }
  }

  async #editProfile(event) {
    if (!event.target.closest("button")) return;

    event.preventDefault();
    this.profileId.value = "";
    this.selected.classList.add("hidden");
    this.search.classList.remove("hidden");
    this.input?.focus();
    await this.#saveCartAttribute("");
  }

  async #saveCartAttribute(profileId) {
    try {
      await fetch(
        FoxTheme.routes.cart_update_url,
        fetchConfig("json", { body: JSON.stringify({ attributes: { donation_profile_id: profileId } }) })
      );
    } catch (error) {
      console.error("Unable to save selected Raisely profile", error);
    }
  }
}

if (!customElements.get("raisely-profile-selector")) {
  customElements.define("raisely-profile-selector", RaiselyProfileSelector);
}
